import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { fileUpload } from '../../../../../services/fileUploadService';
import { Loader } from '../../../../../components/loader';
import { allowedFileTypes } from '../../../../../utils/fileTypeCheck';

const AddProjectItemModal = ({ open, onClose, onSubmit, context = 'overview', link, isLoading = false }) => {
  const getDefaultType = () => {
    if (link) {
      return link.linkUrl.startsWith('http') ? 'link' : 'file';
    }
    return 'link';
  };
  const showTypeSelection = context === 'files';
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const isEditing = Boolean(link);
  const getFileName = (path) => {
    if (!path) return '';
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    const index = filename.indexOf('_');
    return index === -1 ? filename : filename.substring(index + 1);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      itemName: link?.linkTitle || '',
      linkUrl: link?.linkUrl || '',
      selectedType: getDefaultType(),
      s3Key: link?.linkUrl || '',
    },
    validationSchema: Yup.object({
      itemName: Yup.string().required('Please enter a name'),
      linkUrl: Yup.string().when('selectedType', {
        is: 'link',
        then: (schema) =>
          schema
            .test('is-url-valid', 'Please enter a valid URL', (value) => {
              if (!value) return false;
              try {
                new URL(value);
                return true;
              } catch {
                return false;
              }
            })
            .required('Please enter a URL'),
        otherwise: (schema) => schema.notRequired(),
      }),
      s3Key: Yup.string().when('selectedType', {
        is: 'file',
        then: (schema) => schema.required('Please upload a file'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setStatus }) => {
      try {
        const normalizedUrl =
          values.selectedType === 'link'
            ? values.linkUrl.startsWith('http')
              ? values.linkUrl
              : `https://${values.linkUrl}`
            : values.s3Key;

        await onSubmit({
          linkTitle: values.itemName,
          linkUrl: normalizedUrl,
          type: context === 'files' ? 'file' : 'overview',
        });

        handleClose();
      } catch (err) {
        console.error('Submit failed:', err);
        const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
        setStatus({ globalError: msg });
      }
    },
  });

  // Clear error immediately when user starts typing
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
    if (formik.errors[name]) {
      formik.setFieldError(name, undefined);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 1️⃣ Check file size
    if (file.size > 50 * 1024 * 1024) {
      formik.setFieldError('s3Key', 'File size should be less than 50MB');
      return;
    }

    //  Extract extension
    const extension = file.name.split('.').pop().toLowerCase();

    //  Validate extension
    if (!allowedFileTypes[extension]) {
      toast.error('Please select a valid file');
      return;
    }

    try {
      setIsUploading(true);

      // Optional: You can validate `file.type` too
      // if (file.type !== allowedFileTypes[extension]) { ... }

      const res = await fileUpload('project_files', file.name, file);

      setSelectedFile(file);
      formik.setFieldValue('s3Key', res.key);
      formik.setFieldError('s3Key', undefined); // clear error
    } catch (error) {
      formik.setFieldError('s3Key', 'Please select a valid file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setSelectedFile(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? (
          <>
            <Typography variant="h6">Edit</Typography>
            <Typography
              sx={{
                maxWidth: '50%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                verticalAlign: 'bottom',
              }}
            >
              {link.linkTitle}
            </Typography>
          </>
        ) : (
          'Add Link'
        )}
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={formik.handleSubmit} display="flex" flexDirection="column" gap={2} mt={2}>
          {showTypeSelection && !isEditing && (
            <FormControl>
              <FormLabel>Type</FormLabel>
              <RadioGroup
                row
                name="selectedType"
                value={formik.values.selectedType}
                onChange={(e) => {
                  const type = e.target.value;
                  formik.setFieldValue('selectedType', type);
                  setSelectedFile(null);
                  formik.setFieldValue('s3Key', '');
                  formik.setFieldValue('linkUrl', '');
                  formik.setErrors({});
                  formik.setStatus({});
                }}
              >
                <FormControlLabel value="link" control={<Radio />} label="Link" />
                <FormControlLabel value="file" control={<Radio />} label="File" />
              </RadioGroup>
            </FormControl>
          )}

          {formik.values.selectedType === 'file' ? (
            <>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  wordBreak: 'break-all', //  breaks long words anywhere
                  overflowWrap: 'anywhere', // ensures long URLs/names don’t overflow
                  maxWidth: '100%',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input id="file-input" type="file" hidden onChange={handleFileChange} />

                {isUploading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography sx={{ ml: 1 }}>Uploading...</Typography>
                  </Box>
                ) : selectedFile || link?.linkUrl ? (
                  <Box>
                    <Typography variant="h6">{selectedFile?.name || getFileName(link?.linkUrl)}</Typography>
                    {selectedFile && (
                      <Typography variant="body2" color="text.secondary">
                        {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography>Click to select a file</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Max file size: 50MB
                    </Typography>
                  </Box>
                )}
              </Box>

              {formik.errors.s3Key && (
                <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                  {formik.errors.s3Key}
                </Typography>
              )}

              <TextField
                name="itemName"
                label="File Name"
                value={formik.values.itemName}
                onChange={handleFieldChange}
                error={Boolean(formik.errors.itemName)}
                helperText={formik.errors.itemName}
                fullWidth
              />
            </>
          ) : (
            <>
              <TextField
                name="itemName"
                label="Link Title"
                value={formik.values.itemName}
                onChange={handleFieldChange}
                error={Boolean(formik.errors.itemName)}
                helperText={formik.errors.itemName}
                fullWidth
              />
              <TextField
                name="linkUrl"
                label="URL"
                value={formik.values.linkUrl}
                onChange={handleFieldChange}
                error={Boolean(formik.errors.linkUrl)}
                helperText={formik.errors.linkUrl}
                fullWidth
              />
            </>
          )}

          {formik.status?.globalError && <Alert severity="error">{formik.status.globalError}</Alert>}

          <DialogActions>
            <Button onClick={handleClose} color="secondary" disabled={isLoading}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isLoading || isUploading} variant="contained">
              {isLoading ? <Loader size={20} color="inherit" /> : link ? 'Update' : `Add ${formik.values.selectedType}`}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

AddProjectItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  context: PropTypes.oneOf(['overview', 'files']),
  isLoading: PropTypes.bool,
};

export default AddProjectItemModal;
